import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import InventoryService from '../../services/InventoryService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';

const InventoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'Medication',
    quantity: 0,
    unit: 'Tablets',
    reorderLevel: 0,
    supplier: '',
    supplierContact: '',
    lastRestocked: null,
    expiryDate: null,
    location: '',
    description: '',
    notes: '',
    batchNumber: '',
    costPerUnit: 0
  });
  
  useEffect(() => {
    // If in edit mode, fetch inventory item data
    if (isEditMode) {
      const fetchInventoryItemData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const response = await InventoryService.getItemById(parseInt(id));
          const inventoryItem = response.data || response;
          
          // Convert string dates to Date objects
          const lastRestockedDate = inventoryItem.lastRestocked ? new Date(inventoryItem.lastRestocked) : null;
          const expiryDate = inventoryItem.expiryDate ? new Date(inventoryItem.expiryDate) : null;
          
          setFormData({
            ...inventoryItem,
            lastRestocked: lastRestockedDate,
            expiryDate: expiryDate
          });
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching inventory item data:', error);
          setError('Failed to load inventory item data. Please try again later.');
          setLoading(false);
        }
      };
      
      fetchInventoryItemData();
    }
  }, [id, isEditMode]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value === '' ? '' : Number(value)
    });
  };
  
  const handleLastRestockedChange = (date) => {
    setFormData({
      ...formData,
      lastRestocked: date
    });
  };
  
  const handleExpiryDateChange = (date) => {
    setFormData({
      ...formData,
      expiryDate: date
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    // Validate form data
    if (!formData.name || !formData.category || !formData.unit || formData.quantity < 0 || formData.reorderLevel < 0) {
      setError('Please fill in all required fields with valid values.');
      setSaving(false);
      return;
    }
    
    try {
      // Prepare form data for API
      const itemData = {
        ...formData,
        lastRestocked: formData.lastRestocked ? formData.lastRestocked.toISOString() : null,
        expiryDate: formData.expiryDate ? formData.expiryDate.toISOString() : null
      };
      
      if (id) {
        // Update existing item
        await InventoryService.updateItem(parseInt(id), itemData);
      } else {
        // Create new item
        await InventoryService.createItem(itemData);
      }
      
      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/inventory');
      }, 1500);
    } catch (error) {
      console.error('Error saving inventory item:', error);
      setError('Failed to save inventory item. Please try again.');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  const categories = [
    'Medication',
    'Equipment',
    'Supplies',
    'Instruments',
    'Consumables',
    'Other'
  ];
  
  const units = [
    'Tablets',
    'Capsules',
    'Bottles',
    'Boxes',
    'Vials',
    'Ampules',
    'Pieces',
    'Pairs',
    'Rolls',
    'Packs',
    'Kits',
    'Other'
  ];
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/inventory"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Inventory Item' : 'Add Inventory Item'}
          </Typography>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Inventory item {isEditMode ? 'updated' : 'added'} successfully!
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Item Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    label="Category"
                    disabled={saving}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleNumberInputChange}
                  disabled={saving}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel id="unit-label">Unit</InputLabel>
                  <Select
                    labelId="unit-label"
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    label="Unit"
                    disabled={saving}
                  >
                    {units.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Reorder Level"
                  name="reorderLevel"
                  type="number"
                  value={formData.reorderLevel}
                  onChange={handleNumberInputChange}
                  disabled={saving}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  helperText="Minimum quantity before reordering"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supplier"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supplier Contact"
                  name="supplierContact"
                  value={formData.supplierContact}
                  onChange={handleInputChange}
                  disabled={saving}
                  placeholder="Email | Phone"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Last Restocked"
                    value={formData.lastRestocked}
                    onChange={handleLastRestockedChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Expiry Date"
                    value={formData.expiryDate}
                    onChange={handleExpiryDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={saving}
                  placeholder="Storage Room, Shelf, etc."
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Batch Number"
                  name="batchNumber"
                  value={formData.batchNumber}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cost Per Unit"
                  name="costPerUnit"
                  type="number"
                  value={formData.costPerUnit}
                  onChange={handleNumberInputChange}
                  disabled={saving}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Value"
                  type="number"
                  value={(formData.quantity * formData.costPerUnit).toFixed(2)}
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: true
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={saving}
                  placeholder="Storage instructions, warnings, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{ mr: 1 }}
                    component={RouterLink}
                    to="/inventory"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Saving...
                      </>
                    ) : (
                      isEditMode ? 'Update Item' : 'Add Item'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryForm;